import { VACATION_STATUS_LABELS, VACATION_TYPE_LABELS } from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Comments } from '@/components/comments';
import { EntityStatus, RoutePanel } from '@/components/process';
import { Button, ErrorBox, Spinner } from '@/components/ui';
import { api } from '@/lib/api';
import { formatDay, formatMoment, plural } from '@/lib/format';
import { vacationQuery } from '@/lib/queries';

export function VacationPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const q = useQuery(vacationQuery(id));
  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorBox error={q.error} />;
  const v = q.data;

  return (
    <div className="max-w-[1180px]">
      <Link
        to="/vacations"
        className="mb-4 inline-flex items-center gap-1 text-[13px] text-steel hover:text-ink"
      >
        <ChevronLeft className="size-4" aria-hidden />
        Отпуска
      </Link>
      <header className="mb-6">
        <p className="text-[13px] tabular-nums text-steel">Заявление на отпуск №{v.number}</p>
        <h1 className="text-[24px] font-semibold leading-tight tracking-[-0.015em]">
          {v.employee.fullName}: {formatDay(v.startDate)} — {formatDay(v.endDate)}
        </h1>
        <div className="mt-2">
          <EntityStatus status={v.status} label={VACATION_STATUS_LABELS[v.status]} />
        </div>
      </header>

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex min-w-0 flex-col gap-6">
          {v.route && <RoutePanel route={v.route} />}
          <Comments entityType="vacation" entityId={v.id} />
        </div>
        <aside className="flex flex-col gap-6">
          <section className="panel px-5 py-4">
            <dl className="flex flex-col gap-3.5">
              <Row label="Сотрудник">
                {v.employee.fullName}
                {v.employee.departmentName && (
                  <p className="text-[13px] text-steel">{v.employee.departmentName}</p>
                )}
              </Row>
              <Row label="Период">
                {formatDay(v.startDate)} — {formatDay(v.endDate)}
                <p className="text-[13px] text-steel">
                  {v.days} {plural(v.days, ['календарный день', 'календарных дня', 'календарных дней'])}
                </p>
              </Row>
              <Row label="Вид">{VACATION_TYPE_LABELS[v.type]}</Row>
              {v.comment && (
                <Row label="Комментарий">
                  <span className="whitespace-pre-wrap break-words">{v.comment}</span>
                </Row>
              )}
              {v.author.fullName !== v.employee.fullName && <Row label="Подал">{v.author.fullName}</Row>}
              <Row label="Подано">{formatMoment(v.createdAt)}</Row>
            </dl>
          </section>
          {v.permissions.canCancel && (
            <CancelBlock
              url={`/vacations/${v.id}/cancel`}
              question="Отменить заявление?"
              hint={
                v.status === 'pending'
                  ? 'Согласование остановится, задания у руководителей снимутся.'
                  : 'Отпуск уйдёт из графика отсутствий.'
              }
              label="Отменить заявление"
            />
          )}
        </aside>
      </div>
    </div>
  );
}

export function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-[12.5px] text-steel">{label}</dt>
      <dd className="mt-0.5 min-w-0 break-words">{children}</dd>
    </div>
  );
}

export function CancelBlock({
  url,
  question,
  hint,
  label,
}: {
  url: string;
  question: string;
  hint: string;
  label: string;
}) {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const cancel = useMutation({
    mutationFn: () => api(url, { method: 'POST' }),
    onSuccess: async () => {
      setConfirm(false);
      await Promise.all(
        ['vacations', 'requests', 'bpm', 'absences'].map((k) => qc.invalidateQueries({ queryKey: [k] })),
      );
    },
  });
  return confirm ? (
    <div className="panel px-5 py-4">
      <p className="font-medium">{question}</p>
      <p className="mt-1 text-[13px] text-steel">{hint}</p>
      {cancel.error && <p className="mt-1 text-[13px] text-danger">{cancel.error.message}</p>}
      <div className="mt-3 flex gap-2">
        <Button variant="danger" size="sm" loading={cancel.isPending} onClick={() => cancel.mutate()}>
          Да, отменить
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setConfirm(false)}>
          Нет
        </Button>
      </div>
    </div>
  ) : (
    <button
      type="button"
      onClick={() => setConfirm(true)}
      className="self-start px-1 text-[13px] text-steel hover:text-danger"
    >
      {label}
    </button>
  );
}
